import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { SceneManager } from '../managers/SceneManager.js'
import { CameraManager } from '../managers/CameraManager.js'
import { InteractionManager } from '../managers/InteractionManager.js'
import { DebugOverlay } from '../components/DebugOverlay.jsx'
import { useDeepLinking } from '../hooks/useDeepLinking.jsx'

export const WebCrafterContext = createContext(null)

/**
 * Provider component for WebCrafter scene state and managers.
 */
export function WebCrafterProvider({ children, isDebugging: initialIsDebugging = false })
{
  /* Loading State */
  const [ sceneManager, setSceneManager ] = useState(null)
  const [ cameraManager, setCameraManager ] = useState(null)
  const [ interactionManager, setInteractionManager ] = useState(null)

  const [ siteData, setSiteData ] = useState(null)
  const [ api, setApi ] = useState(null)

  const [ isLoaded, setIsLoaded ] = useState(false)
  const [ loadingProgress, setLoadingProgress ] = useState(0)
  const [ managersReady, setManagersReady ] = useState(false) // Tracks if managers are initialized

  // Initialize debug state from URL or prop
  const [ isDebugging, setIsDebugging ] = useState(() =>
  {
    if (typeof window !== 'undefined')
    {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('debug') === 'true' || initialIsDebugging
    }
    return initialIsDebugging
  })


  // Cleanup managers on change or unmount
  React.useEffect(() =>
  {
    return () =>
    {
      if (cameraManager && cameraManager.dispose)
      {
        cameraManager.dispose()
      }
    }
  }, [ cameraManager ])

  const setManagers = useCallback((newSceneManager, newCameraManager, newInteractionManager) =>
  {
    setSceneManager(newSceneManager)
    setCameraManager(newCameraManager)
    setInteractionManager(newInteractionManager)
    if (newSceneManager) setSiteData(newSceneManager.getSiteData())
    setManagersReady(true)
  }, [])

  const initializeManagers = useCallback(({
    scene, controls, animations, actions, mixer, camera,
    setShowPopup, setPopupContent, scrollState = null
  }) =>
  {
    // STALE MANAGER FIX:
    // If we have a manager, ensure it's managing the CURRENT scene.
    // If the scene object changed (e.g. fast refresh or new GLTF load),
    // we MUST re-initialize to avoid operating on a stale/detached scene.
    if (sceneManager && sceneManager.scene === scene) return

    const newSceneManager = new SceneManager(scene, controls, animations, actions, mixer)
    const newCameraManager = new CameraManager(newSceneManager, controls, camera, scrollState)
    const newInteractionManager = new InteractionManager(
      setShowPopup, setPopupContent,
      newCameraManager.goToSceneZoneByName,
      newSceneManager.playAnimation,
      newSceneManager.playSound
    )

    setManagers(newSceneManager, newCameraManager, newInteractionManager)

    const siteData = newSceneManager.getSiteData()
    setApi({
      goToSceneZoneByIndex: newCameraManager.goToSceneZoneByIndex,
      goToSceneZoneByName: newCameraManager.goToSceneZoneByName,
      getSceneManager: () => newSceneManager,
      getCameraManager: () => newCameraManager,
      getInteractionManager: () => newInteractionManager,
      getSiteData: () => siteData,
      subscribe: newCameraManager.subscribe.bind(newCameraManager),
    })

    setManagersReady(true)
  }, [ sceneManager, setManagers ])

  // Router: Map URL paths to Scene Zones and sync state
  useDeepLinking(sceneManager, cameraManager, managersReady, isLoaded, isDebugging)

  const disableDebugMode = useCallback(() =>
  {
    setIsDebugging(false)
    if (typeof window !== 'undefined')
    {
      const url = new URL(window.location)
      url.searchParams.delete('debug')
      window.history.replaceState({}, '', url)
    }
  }, [])

  const value = useMemo(() => ({
    sceneManager,
    cameraManager,
    interactionManager,
    siteData,
    isDebugging,
    setManagers,
    initializeManagers,
    api,
    setApi,
    disableDebugMode,
    // Loading state
    isLoaded,
    setIsLoaded,
    loadingProgress,
    setLoadingProgress,
    managersReady
  }), [
    sceneManager,
    cameraManager,
    interactionManager,
    siteData,
    isDebugging,
    setManagers,
    initializeManagers,
    api,
    disableDebugMode,
    // Loading state dependencies
    isLoaded,
    loadingProgress,
    managersReady
  ])

  return (
    <WebCrafterContext.Provider value={value}>
      {children}
      {isDebugging && <DebugOverlay onDisable={disableDebugMode} />}
    </WebCrafterContext.Provider>
  )
}

